import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';

type BatchProgressState = {
  completed: number;
  total: number;
  isComplete: boolean;
}

type Operation = {
  id?: string;
  __typename: string;
  [key: string]: any;
}

type UpdateOperation = {
  id: string;
  operation: Operation;
}

type OperationTypesByPatchType = {
  _SetBundPrice: string[];
  _RmvBund: string[];
  _RmvProdVar: string[];
  _SetProdPrices: string[];
  [key: string]: string[];
}

type PendingChange = {
  id: string;
  type: string;
  data: {
    patchIds: string[];
    setOperations: { minimal: Operation; enriched: Operation }[];
    removeOperations: { minimal: Operation; enriched: Operation }[];
  };
}

type PatchesState = {
  batchProgress: BatchProgressState;
  patches: any[];
  patchObject: any | null;
  patchType: string | null;
  operationTypes: OperationTypesByPatchType;
  
  // New state for patch management enhancements
  hasFetchedPatches: boolean;
  hasUnsavedChanges: boolean;
  pendingChanges: PendingChange;
}

const initialState: PatchesState = {
  batchProgress: {
    completed: 0,
    total: 0,
    isComplete: false
  },
  patches: [],
  patchObject: null,
  patchType: null,
  operationTypes: {
    _SetBundPrice: [
      'SetBundlePriceMenuPatchOperation'
    ],
    _RmvBund: [
      'RemoveBundleMenuPatchOperation'
    ],
    _RmvProdVar: [
      'RemoveProductMenuPatchOperation',
      'RemoveVariantMenuPatchOperation',
      'RemoveModifierFromSlotPatchOperation',
      'RemoveModifierMenuPatchOperation',
      'SetModifierWeightIsExcludedFromVariantMenuPatchOperation',
    ],
    _SetProdPrices: [
      'SetVariantPriceMenuPatchOperation',
      'SetModifierPriceRangesMenuPatchOperation',
      'SetModifierWeightPriceMenuPatchOperation'
    ]
  },
  
  // Initialize new state variables
  hasFetchedPatches: false,
  hasUnsavedChanges: false,
  pendingChanges: {
    id: '',
    type: '',
    data: {
      patchIds: [],
      setOperations: [],
      removeOperations: []
    }
  }
};

export const patchesSlice = createSlice({
  name: 'patches',
  initialState,
  reducers: {
    updateBatchProgress: (state, action: PayloadAction<{ completed: number; total: number }>) => {
      const { completed, total } = action.payload;
      state.batchProgress.completed = completed;
      state.batchProgress.total = total;
      state.batchProgress.isComplete = completed === total && total > 0;
    },
    resetBatchProgress: (state) => {
      state.batchProgress = initialState.batchProgress;
    },
    setPatches: (state, action: PayloadAction<any[]>) => {
      state.patches = action.payload;
    },
    setPatchObject: (state, action: PayloadAction<any>) => {
      state.patchObject = action.payload;
    },
    setPatchType: (state, action: PayloadAction<string>) => {
      state.patchType = action.payload;
    },
    addPatchOperations: (state, action: PayloadAction<{
      setOperations?: Operation[];
      removeOperations?: Operation[];
      updateOperations?: UpdateOperation[];
    }>) => {
      const { setOperations, removeOperations, updateOperations } = action.payload;
      
      if (setOperations && state.patchObject) {
        if (!state.patchObject.operations) {
          state.patchObject.operations = [];
        }
        state.patchObject.operations = [...state.patchObject.operations, ...setOperations];
      }
      
      if (removeOperations && state.patchObject) {
        if (!state.patchObject.operations) {
          state.patchObject.operations = [];
        }
        state.patchObject.operations = [...state.patchObject.operations, ...removeOperations];
      }
      
      if (updateOperations && state.patchObject && state.patchObject.operations) {
        updateOperations.forEach(update => {
          const index = state.patchObject.operations.findIndex(
            (op: Operation) => op.id === update.id
          );
          if (index !== -1) {
            state.patchObject.operations[index] = {
              ...state.patchObject.operations[index],
              ...update.operation,
              id: update.id
            };
          }
        });
      }
    },
    
    // New reducers for patch management enhancements
    setHasFetchedPatches: (state, action: PayloadAction<boolean>) => {
      state.hasFetchedPatches = action.payload;
    },
    
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    
    addPendingChange: (state, action: PayloadAction<{
        type: string;
        data: {
          patchIds: string[];
          setOperations: { minimal: Operation; enriched: Operation }[];
          removeOperations: { minimal: Operation; enriched: Operation }[];
        };
    }>) => {
      const { type, data } = action.payload;
      
      // If there's an existing pending change, merge the new data
      if (state.pendingChanges.id) {
        state.pendingChanges = {
          ...state.pendingChanges,
          type: 'Multiple Operations',
          data: {
            patchIds: Array.from(new Set([...state.pendingChanges.data.patchIds, ...data.patchIds])),
              setOperations: [...state.pendingChanges.data.setOperations, ...data.setOperations],
              removeOperations: [...state.pendingChanges.data.removeOperations, ...data.removeOperations]
          }
        };
      } else {
        // If no existing pending change, create a new one
        state.pendingChanges = {
          id: `change-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
          type,
          data
        };
      }
      
      // Always set hasUnsavedChanges to true when any operations are present
      const hasOperations = state.pendingChanges.data.setOperations.length > 0 || state.pendingChanges.data.removeOperations.length > 0;
      state.hasUnsavedChanges = hasOperations;
    },

    removeOperationFromPending: (state, action: PayloadAction<{ operationType: 'set' | 'remove'; operationIndex: number }>) => {
      const { operationType, operationIndex } = action.payload;
      
        if (operationType === 'set') {
          state.pendingChanges.data.setOperations.splice(operationIndex, 1);
        } else if (operationType === 'remove') {
          state.pendingChanges.data.removeOperations.splice(operationIndex, 1);
        }
      
      // Update hasUnsavedChanges based on remaining operations
      const hasOperations = state.pendingChanges.data.setOperations.length > 0 || state.pendingChanges.data.removeOperations.length > 0;

      // If no operations left, clear the pending change
      if (!hasOperations) {
        state.pendingChanges = initialState.pendingChanges;
        state.hasUnsavedChanges = false;
      } else {
        state.hasUnsavedChanges = true;
      }
    },
    
    clearPendingChanges: (state) => {
      state.pendingChanges = initialState.pendingChanges;
      state.hasUnsavedChanges = false;
    }
  },
  // Handle the custom action dispatched from the RTK Query endpoint
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type === 'patches/batchProgress',
      (state, action: any) => {
        if (action.payload) {
          const { completed, total } = action.payload;
          state.batchProgress.completed = completed;
          state.batchProgress.total = total;
          state.batchProgress.isComplete = completed === total && total > 0;
        }
      }
    );
  }
});

export const { 
  updateBatchProgress, 
  resetBatchProgress, 
  setPatches, 
  setPatchObject, 
  setPatchType,
  addPatchOperations,
  
  // New actions for patch management enhancements
  setHasFetchedPatches,
  setHasUnsavedChanges,
  addPendingChange,
  removeOperationFromPending,
  clearPendingChanges
} = patchesSlice.actions;

// Selectors
export const selectBatchProgress = (state: RootState) => state.patches.batchProgress;
export const selectPatches = (state: RootState) => state.patches.patches;

// New selectors for patch management enhancements
export const selectHasFetchedPatches = (state: RootState) => state.patches.hasFetchedPatches;
export const selectHasUnsavedChanges = (state: RootState) => state.patches.hasUnsavedChanges;
export const selectPendingChanges = (state: RootState) => state.patches.pendingChanges;

export default patchesSlice.reducer;
